import React, { useEffect, useMemo, useState } from "react";
import {
  MdArrowDropUp,
  MdOutlineCalendarToday,
  MdBarChart,
} from "react-icons/md";
import Card from "components/card";
import {
  lineChartDataTotalSpent as defaultSeries,
  lineChartOptionsTotalSpent as baseOptions,
} from "variables/charts";
import LineChart from "components/charts/LineChart";
import { fetchRevenueMonthly } from "utils/api";

const formatINR = (n) => `₹${(n ?? 0).toLocaleString("en-IN")}`;

const TotalSpent = () => {
  const now = new Date();
  const defaultYear = now.getFullYear(); // e.g., 2026

  const [year] = useState(defaultYear); // keep constant unless you add a year selector
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [categories, setCategories] = useState(baseOptions.xaxis.categories);
  const [series, setSeries] = useState(defaultSeries);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);

        // If your “paid” status is not 1, try include_pending=true to verify non-zero data.
        const data = await fetchRevenueMonthly({ year, include_pending: false });

        if (!mounted) return;

        // Map API output
        const cats = data?.labels_pretty ?? baseOptions.xaxis.categories;
        const vals =
          Array.isArray(data?.series) && data.series.length === 12
            ? data.series
            : defaultSeries[0].data;
        const total = Number(data?.total_revenue ?? 0);

        setCategories(cats);
        setSeries([{ name: "Revenue", data: vals, color: "#4318FF" }]);
        setTotalRevenue(total);
      } catch (err) {
        console.error("Failed to fetch /admin/revenue_monthly:", err);
        // Keep defaults on error so UI doesn't break
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [year]);

  // Don’t mutate base options; override categories via a cloned object
  const options = useMemo(() => {
    return {
      ...baseOptions,
      xaxis: {
        ...baseOptions.xaxis,
        categories,
      },
      // Optionally ensure currency tooltip (if not already set)
      tooltip: {
        ...baseOptions.tooltip,
        y: { formatter: (val) => `₹${(val ?? 0).toLocaleString("en-IN")}` },
      },
    };
  }, [categories]);

  return (
    <Card extra="!p-3 sm:!p-4 text-center min-w-0">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between lg:flex-nowrap">
        <button className="linear flex items-center justify-center gap-2 rounded-lg bg-lightPrimary p-1.5 sm:p-2 text-gray-600 transition duration-200 hover:cursor-pointer hover:bg-gray-100 active:bg-gray-200 dark:bg-navy-700 dark:hover:opacity-90 dark:active:opacity-80">
          <MdOutlineCalendarToday className="shrink-0 h-4 w-4 sm:h-5 sm:w-5" />
          <span className="text-xs sm:text-sm font-medium text-gray-600">This month</span>
        </button>
        <button className="!linear z-[1] flex shrink-0 items-center justify-center rounded-lg bg-lightPrimary p-1.5 sm:p-2 text-brand-500 !transition !duration-200 hover:bg-gray-100 active:bg-gray-200 dark:bg-navy-700 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/10">
          <MdBarChart className="h-4 w-4 sm:h-5 sm:w-5" />
        </button>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between lg:flex-nowrap">
  <div className="flex flex-col flex-shrink-0">
    <p className="mt-2 sm:mt-3 text-lg sm:text-2xl md:text-3xl font-bold text-navy-700 dark:text-white">
      {loading ? "…" : formatINR(totalRevenue)}
    </p>
    <div className="flex flex-col items-start">
      <p className="mt-0.5 text-xs sm:text-sm text-gray-600">Total Spent</p>
      <div className="flex flex-row items-center">
        <MdArrowDropUp className="font-medium text-green-500 h-4 w-4" />
        <p className="text-xs font-bold text-green-500"> +2.45% </p>
      </div>
    </div>
  </div>

  <div className="w-full flex-1 max-w-full h-[200px] sm:h-[240px] md:h-[280px]">
    <LineChart options={options} series={series} />
  </div>
</div>

    </Card>
  );
};

export default TotalSpent;